import { Action, Reducer, ActionCreator, combineReducers } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { batch } from 'react-redux';
import { Moment } from 'moment';

import { RootState } from '..';
import { IWebinarDetailState } from '../../interfaces/states/IWebinarDetailState';
import { IWebinar } from '../../common/model/webinar.model';
import { IWebinarsService } from '../../services';
import { IErrors } from '../../common/model/errors.model';

enum Type {
  RESET = '@@webinarDetail/RESET',
  MAKE_READY_FOR_RENDERING = '@@webinarDetail/MAKE_READY_FOR_RENDERING',
  SET_WEBINAR = '@@webinarDetail/SET_WEBINAR',
  SET_WEBINAR_INITIAL = '@@webinarDetail/SET_WEBINAR_INITIAL',
  SET_WEBINAR_NAME = '@@webinarDetail/SET_WEBINAR_NAME',
  SET_WEBINAR_SPEAKERS = '@@webinarDetail/SET_WEBINAR_SPEAKERS',
  SET_WEBINAR_DATE = '@@webinarDetail/SET_WEBINAR_DATE',
  SET_WEBINAR_DESCRIPTION = '@@webinarDetail/SET_WEBINAR_DESCRIPTION',
  SET_WEBINAR_VIDEO_URL = '@@webinarDetail/SET_WEBINAR_VIDEO_URL',
  SET_WEBINAR_VIDEO = '@@webinarDetail/SET_WEBINAR_VIDEO',
  SET_WEBINAR_ACTIVE = '@@webinarDetail/SET_ACTIVE',
  SET_IS_VIEW = '@@webinarDetail/SET_IS_VIEW',
  SET_IS_NEW = '@@webinarDetail/SET_IS_NEW',
  SET_ERRORS = '@@webinarDetail/SET_ERRORS'
}

const defaultWebinar: IWebinar = {
  id: null,
  name: null,
  speakers: null,
  date: null,
  description: '',
  videoUrl: null,
  video: null,
  active: true
};

const initialState: IWebinarDetailState = {
  isReadyForRendering: false,
  webinar: defaultWebinar,
  webinarInitial: defaultWebinar,
  isView: null, // must be !== null when isReadyForRendering === true
  isNew: null, // must be !== null when isReadyForRendering === true
  errors: {}
};

// Actions

export type Actions = ResetAction | MakeReadyForRenderingAction | SetWebinarAction | SetWebinarNameAction | SetWebinarSpeakersAction | SetWebinarDateAction | SetWebinarDescriptionAction
  | SetWebinarVideoUrlAction | SetWebinarVideoAction | SetWebinarActiveAction | SetWebinarInitialAction | SetIsViewAction | SetErrorsAction;

export type ResetAction = Action<Type.RESET>

export interface MakeReadyForRenderingAction extends Action<Type.MAKE_READY_FOR_RENDERING> {
  isView: boolean;
  isNew: boolean;
}

export interface SetWebinarAction extends Action<Type.SET_WEBINAR> {
  webinar: IWebinar;
}

export interface SetWebinarNameAction extends Action<Type.SET_WEBINAR_NAME> {
  name: string;
}

export interface SetWebinarSpeakersAction extends Action<Type.SET_WEBINAR_SPEAKERS> {
  speakers: string;
}

export interface SetWebinarDateAction extends Action<Type.SET_WEBINAR_DATE> {
  date: Moment;
}

export interface SetWebinarDescriptionAction extends Action<Type.SET_WEBINAR_DESCRIPTION> {
  description: string;
}

export interface SetWebinarVideoUrlAction extends Action<Type.SET_WEBINAR_VIDEO_URL> {
  videoUrl: string;
}

export interface SetWebinarVideoAction extends Action<Type.SET_WEBINAR_VIDEO> {
  video: File;
}

export interface SetWebinarActiveAction extends Action<Type.SET_WEBINAR_ACTIVE> {
  active: boolean;
}

export interface SetWebinarInitialAction extends Action<Type.SET_WEBINAR_INITIAL> {
  webinarInitial: IWebinar;
}

export interface SetIsViewAction extends Action<Type.SET_IS_VIEW> {
  isView: boolean;
}

export interface SetIsNewAction extends Action<Type.SET_IS_NEW> {
  isNew: boolean;
}

export interface SetErrorsAction extends Action<Type.SET_ERRORS> {
  errors: IErrors;
}

export const reset: ActionCreator<ResetAction> = () => ({
  type: Type.RESET
});

export const makeReadyForRendering: ActionCreator<MakeReadyForRenderingAction> = (isViewLocal: boolean, isNewLocal: boolean) => ({
  type: Type.MAKE_READY_FOR_RENDERING,
  isView: isViewLocal,
  isNew: isNewLocal
});

export const setWebinar: ActionCreator<SetWebinarAction> = (webinarLocal: IWebinar) => ({
  type: Type.SET_WEBINAR,
  webinar: webinarLocal
});

export const setWebinarName: ActionCreator<SetWebinarNameAction> = (name: string) => ({
  type: Type.SET_WEBINAR_NAME,
  name
});

export const setWebinarSpeakers: ActionCreator<SetWebinarSpeakersAction> = (speakers: string) => ({
  type: Type.SET_WEBINAR_SPEAKERS,
  speakers
});

export const setWebinarDate: ActionCreator<SetWebinarDateAction> = (date: Moment) => ({
  type: Type.SET_WEBINAR_DATE,
  date
});

export const setWebinarDescription: ActionCreator<SetWebinarDescriptionAction> = (description: string) => ({
  type: Type.SET_WEBINAR_DESCRIPTION,
  description
});

export const setWebinarVideoUrl: ActionCreator<SetWebinarVideoUrlAction> = (videoUrl: string) => ({
  type: Type.SET_WEBINAR_VIDEO_URL,
  videoUrl
});

export const setWebinarVideo: ActionCreator<SetWebinarVideoAction> = (video: File) => ({
  type: Type.SET_WEBINAR_VIDEO,
  video
});

export const setWebinarActive: ActionCreator<SetWebinarActiveAction> = (active: boolean) => ({
  type: Type.SET_WEBINAR_ACTIVE,
  active
});

export const setWebinarInitial: ActionCreator<SetWebinarInitialAction> = (webinarInitialLocal: IWebinar) => ({
  type: Type.SET_WEBINAR_INITIAL,
  webinarInitial: webinarInitialLocal
});

export const setIsView: ActionCreator<SetIsViewAction> = (isViewLocal: boolean) => ({
  type: Type.SET_IS_VIEW,
  isView: isViewLocal
});

export const setIsNew: ActionCreator<SetIsNewAction> = (isNewLocal: boolean) => ({
  type: Type.SET_IS_NEW,
  isNew: isNewLocal
});

export const fetchWebinar: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (id: string, webinarsService: IWebinarsService) => (
  async (dispatch) => {
    try {
      const webinarLocal = await webinarsService.fetch(id);

      batch(() => {
        dispatch(setWebinar(webinarLocal));
        dispatch(setWebinarInitial(webinarLocal));
      });
    } catch (error) {
      // TODO: manejar error
    }
  }
);

export const resetWebinar: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = () => (
  async (dispatch, getState) => {
    dispatch(setWebinar(getState().webinarDetailStore.webinarInitial));
  }
);

export const saveWebinar: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (webinarLocal: IWebinar) => (
  async (dispatch) => {
    dispatch(setWebinar(webinarLocal));
    dispatch(setWebinarInitial(webinarLocal));
  }
);

export const setErrors: ActionCreator<SetErrorsAction> = (errorsLocal: IErrors) => ({
  type: Type.SET_ERRORS,
  errors: errorsLocal
});

// Reducers
const isReadyForRendering: Reducer<boolean> = (state = initialState.isReadyForRendering, action) => {
  switch (action.type) {
    case Type.RESET:
      return initialState.isReadyForRendering;
    case Type.MAKE_READY_FOR_RENDERING:
      return true;
    default:
      return state;
  }
};

const webinar: Reducer<IWebinar> = (state = initialState.webinar, action) => {
  switch (action.type) {
    case Type.RESET:
      return {
        ...initialState.webinar
      };
    case Type.SET_WEBINAR:
      return (action as SetWebinarAction).webinar;
    case Type.SET_WEBINAR_NAME:
      return { ...state, name: (action as SetWebinarNameAction).name };
    case Type.SET_WEBINAR_SPEAKERS:
      return { ...state, speakers: (action as SetWebinarSpeakersAction).speakers };
    case Type.SET_WEBINAR_DATE:
      return { ...state, date: (action as SetWebinarDateAction).date };
    case Type.SET_WEBINAR_DESCRIPTION:
      return { ...state, description: (action as SetWebinarDescriptionAction).description };
    case Type.SET_WEBINAR_VIDEO_URL:
      return { ...state, videoUrl: (action as SetWebinarVideoUrlAction).videoUrl };
    case Type.SET_WEBINAR_VIDEO:
      return { ...state, video: (action as SetWebinarVideoAction).video };
    case Type.SET_WEBINAR_ACTIVE:
      return { ...state, active: (action as SetWebinarActiveAction).active };
    default:
      return state;
  }
};

const webinarInitial: Reducer<IWebinar> = (state = initialState.webinarInitial, action) => {
  switch (action.type) {
    case Type.RESET:
      return {
        ...initialState.webinarInitial
      };
    case Type.SET_WEBINAR_INITIAL:
      return (action as SetWebinarInitialAction).webinarInitial;
    default:
      return state;
  }
};

const isView: Reducer<boolean> = (state = initialState.isView, action) => {
  switch (action.type) {
    case Type.RESET:
      return initialState.isView;
    case Type.MAKE_READY_FOR_RENDERING:
      return (action as MakeReadyForRenderingAction).isView;
    case Type.SET_IS_VIEW:
      return (action as SetIsViewAction).isView;
    default:
      return state;
  }
};

const isNew: Reducer<boolean> = (state = initialState.isNew, action) => {
  switch (action.type) {
    case Type.RESET:
      return initialState.isNew;
    case Type.MAKE_READY_FOR_RENDERING:
      return (action as MakeReadyForRenderingAction).isNew;
    case Type.SET_IS_NEW:
      return (action as SetIsNewAction).isNew;
    default:
      return state;
  }
};

const errors: Reducer<IErrors> = (state = initialState.errors, action) => {
  switch (action.type) {
  case Type.RESET:
    return initialState.errors;
  case Type.SET_ERRORS:
    return (action as SetErrorsAction).errors;
  default:
    return state;
  }
};

export const webinarDetailStore = combineReducers({
  isReadyForRendering,
  webinar,
  webinarInitial,
  isView,
  isNew,
  errors
});
